/**
 * src/TechHubPage.js
 *
 * VISINVOKE Tech Hub - entry page.
 *
 * Replaces the former standalone tool hub. This page runs only inside the
 * VISINVOKE platform behind a signed-in account. It presents grouped
 * technician tool cards and resolves the content-level Technician Access
 * state. The route itself is sign-in gated by PrivateRoute; this page decides
 * what a signed-in user with or without Technician Access sees.
 *
 * Technician Access is the technician_access entitlement flag on the user
 * object. No approval workflow, no account creation, no device linking.
 *
 * External Tool Downloads catalogue (Stage 2): the single external-tool area on
 * this page. Every card is visible to every signed-in user so general users can
 * see what technician access offers. Working official supplier links are gated
 * by Technician Access. Technician accounts open the official supplier, vendor
 * or project page in a new tab through the VISINVOKE Desktop Command Center.
 * General users see the same cards with a disabled Technician Access Required
 * button and never receive the clickable official URL. VISINVOKE does not host,
 * mirror, download, install or run any third-party tool.
 */
import React, { useState, useEffect } from 'react';
import { glassSurface, viGlassFill, viGlassBorder, viGlassShadow } from './config/glassTheme';
import { useNavigate } from 'react-router-dom';
import ProductNav from './components/ProductNav';
import { useAuth } from './AuthContext';
import { hexA } from './RegistryComponents';
import { recordTechActivity } from './techActivity';
import API from './api';
import {
  TECH_HUB_GROUPS,
  TECH_HUB_BASE_ROUTE,
  getTechHubStatusColor,
  getLauncherState,
  EXTERNAL_TOOL_GROUPS,
  EXTERNAL_TOOLS_SECTION,
  getExternalToolAccessState,
  EXTERNAL_TOOL_LOCKED_LABEL,
  externalToolCount,
} from './techHubConfig';

// Responsive breakpoints for the External Tool Downloads grid. Mirrors the
// ProductNav vw pattern (single source of truth) so the page transitions as one
// system. desktop (>= 1140) = 4 cards per row; tablet (>= 720) = 2 per row;
// mobile (< 720) = 1 per row.
const EXT_BP_4COL = 1140;
const EXT_BP_2COL = 720;

function extColumnsForWidth(vw) {
  if (vw >= EXT_BP_4COL) return 4;
  if (vw >= EXT_BP_2COL) return 2;
  return 1;
}

// Decide whether an external tool card should render its safety badge alongside
// the licence/source badge. The licence/source badge is always shown. The
// safety badge is a pure display filter (no data change): it is hidden when it
// would duplicate meaning already carried by the licence badge. Specifically,
// hide the safety badge when it is empty, when it is exactly "Official Link
// Only" (already implied by every "... / Official Link Only" licence badge), or
// when it is identical to the licence badge. Any other safety badge that adds
// real meaning (Advanced Tool, Use With Caution, Vendor Tool, Security Tool,
// Licence Required, and so on) is still shown.
function showSafetyBadge(licenceBadge, safetyBadge) {
  const s = safetyBadge ? String(safetyBadge).trim() : '';
  const l = licenceBadge ? String(licenceBadge).trim() : '';
  if (s === '') return false;
  if (s === 'Official Link Only') return false;
  if (s === l) return false;
  return true;
}

function darkenHex(hex, factor) {
  let h = (hex || '#888888').replace('#', '');
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  const r = Math.round(parseInt(h.substring(0, 2), 16) * factor);
  const g = Math.round(parseInt(h.substring(2, 4), 16) * factor);
  const b = Math.round(parseInt(h.substring(4, 6), 16) * factor);
  const to2 = n => Math.max(0, Math.min(255, n)).toString(16).padStart(2, '0');
  return '#' + to2(r) + to2(g) + to2(b);
}

// ToolCard (correction stage). Every resource is visible to every signed-in
// user. The card never runs a utility in the browser; its button opens the
// launcher-state resource-detail page. The action label reflects the viewer's
// Technician Access: normal users see "Technician Access Required" and a
// request-access hint; technician users see the Desktop Command Center state.
function ToolCard({ tool, accent, hasAccess, onOpen }) {
  const state = getLauncherState(tool, hasAccess);
  const bg = viGlassFill(accent, accent, accent);

  return (
    <div style={{ ...styles.card, background: bg, border: viGlassBorder(accent, 1.5), boxShadow: viGlassShadow(accent, accent, accent, false) }}>
      <div style={styles.cardSheen} />
      <div style={styles.cardHeader}>
        <div style={styles.cardIcon}>{tool.icon}</div>
        <div style={styles.cardTitle}>{tool.title}</div>
      </div>
      <div style={styles.cardDesc}>{tool.description}</div>
      <div style={{ ...styles.cardFooter, borderTop: '1px solid ' + hexA('#ffffff', 0.18) }}>
        <div style={styles.statusRow}>
          <span style={{ ...styles.statusDot, background: state.color, boxShadow: '0 0 8px ' + hexA(state.color, 0.7) }} />
          <span style={{ ...styles.statusText, color: state.color }}>{state.label}</span>
        </div>
        <div style={styles.cardHint}>{state.sub}</div>
        <button
          style={{
            ...styles.openBtn,
            ...glassSurface(accent, accent, accent),
            cursor: 'pointer',
          }}
          onClick={() => onOpen(tool.slug)}
        >
          {hasAccess ? 'Open in Desktop Command Center' : 'View details'}
        </button>
      </div>
    </div>
  );
}

// ExternalToolCard. Fixed-zone layout so every card in a grid aligns: the icon
// and title zone, category zone, description zone, technician-use zone, and
// licence/safety/access badge zone each reserve a consistent vertical block,
// and the button is bottom-aligned. Content is clamped so a longer title or
// description can never push the button out of alignment.
//
// Access: for general users the button is disabled, reads Technician Access
// Required, and the official URL is not rendered anywhere in the card. For
// technician accounts the button becomes an anchor that opens the official
// supplier/vendor/project page in a new tab with safe rel attributes.
function ExternalToolCard({ tool, accent, hasAccess }) {
  const access = getExternalToolAccessState(tool, hasAccess);
  const bg = viGlassFill(accent, accent, accent);

  return (
    <div style={{ ...extStyles.card, background: bg, border: viGlassBorder(accent, 1.5), boxShadow: viGlassShadow(accent, accent, accent, false) }}>
      <div style={extStyles.cardSheen} />
      <div style={{ ...extStyles.accentBar, background: hexA('#ffffff', 0.28) }} />

      <div style={extStyles.zoneHeader}>
        <div style={extStyles.headerIcon}>{tool.icon}</div>
        <div style={extStyles.headerTitle}>{tool.name}</div>
      </div>

      <div style={extStyles.zoneCategory}>
        <span style={{ ...extStyles.categoryPill, ...glassSurface(accent, accent, accent) }}>{tool.category}</span>
      </div>

      <div style={extStyles.zoneDesc}>{tool.description}</div>

      <div style={extStyles.zoneTechUse}>
        <div style={extStyles.techUseLabel}>Technician use</div>
        <div style={extStyles.techUseText}>{tool.technicianUse}</div>
      </div>

      <div style={extStyles.zoneBadges}>
        <span style={{ ...extStyles.badge, ...glassSurface(accent, accent, accent) }}>{tool.licenceBadge}</span>
        {showSafetyBadge(tool.licenceBadge, tool.safetyBadge) ? (
          <span style={{ ...extStyles.badge, ...glassSurface(accent, accent, accent) }}>{tool.safetyBadge}</span>
        ) : null}
      </div>

      <div style={extStyles.zoneFooter}>
        <div style={extStyles.footerHint}>
          {hasAccess ? 'Opens the official supplier page in a new tab. Leaves VISINVOKE.' : 'Official link is unlocked with Technician Access.'}
        </div>
        {hasAccess ? (
          <a
            href={tool.officialUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => recordTechActivity({
              event_type:  'DOWNLOAD_LINK_CLICKED',
              item_type:   'download',
              item_name:   tool.name,
              item_slug:   tool.id,
              source:      'download_page',
              source_page: 'tech_hub_external_tools',
              metadata:    { category: tool.category },
            })}
            style={{ ...extStyles.actionBtn, ...glassSurface(accent, accent, accent), cursor: 'pointer', textDecoration: 'none' }}
          >
            {access.label}
          </a>
        ) : (
          <button
            type="button"
            disabled={true}
            style={{ ...extStyles.actionBtn, ...glassSurface('#A66D05', '#fbbf24', '#FBBF24'), cursor: 'not-allowed' }}
          >
            {EXTERNAL_TOOL_LOCKED_LABEL}
          </button>
        )}
      </div>
    </div>
  );
}

// The External Tool Downloads section. Rendered after the native VISINVOKE
// technician tool groups. One catalogue, grouped and colour-themed from
// EXTERNAL_TOOL_GROUPS, with the section copy from EXTERNAL_TOOLS_SECTION.
function ExternalToolsSection({ hasAccess, columns }) {
  const gridStyle = { ...extStyles.grid, gridTemplateColumns: 'repeat(' + columns + ', minmax(0, 1fr))' };

  return (
    <div style={extStyles.section}>
      <div style={extStyles.sectionHead}>
        <div style={extStyles.sectionEyebrow}>Technician Access</div>
        <h2 style={extStyles.sectionTitle}>{EXTERNAL_TOOLS_SECTION.title}</h2>
        <p style={extStyles.sectionHelper}>{EXTERNAL_TOOLS_SECTION.helper}</p>
        <p style={extStyles.sectionHelper}>{EXTERNAL_TOOLS_SECTION.purposeNote}</p>
        <p style={extStyles.sectionCount}>{externalToolCount() + ' official supplier tools across ' + EXTERNAL_TOOL_GROUPS.length + ' groups.'}</p>
      </div>

      <div style={{ ...extStyles.notice, border: '1px solid ' + hexA('#38bdf8', 0.4), background: hexA('#38bdf8', 0.10) }}>
        <span style={extStyles.noticeIcon}>{'\u{1F6E1}'}</span>
        <div>
          <div style={extStyles.noticeTitle}>Technician Access Required</div>
          <p style={extStyles.noticeText}>{EXTERNAL_TOOLS_SECTION.accessNotice}</p>
        </div>
      </div>

      {!hasAccess ? (
        <div style={{ ...extStyles.lockedHelper, border: '1px solid ' + hexA('#fbbf24', 0.4), background: hexA('#fbbf24', 0.10) }}>
          <span style={extStyles.lockedHelperIcon}>{'\u{1F512}'}</span>
          <p style={extStyles.lockedHelperText}>{EXTERNAL_TOOLS_SECTION.lockedHelper}</p>
        </div>
      ) : null}

      <div style={extStyles.groups}>
        {EXTERNAL_TOOL_GROUPS.map(group => (
          <div key={group.id} style={extStyles.group}>
            <div style={{ ...extStyles.groupHeader, borderLeft: '4px solid ' + group.accent }}>
              <span style={{ ...extStyles.groupIcon, color: group.accent }}>{group.icon}</span>
              <div>
                <h3 style={extStyles.groupTitle}>{group.title}</h3>
                <p style={extStyles.groupHelper}>{group.helperText}</p>
              </div>
            </div>
            <div style={gridStyle}>
              {group.tools.map(tool => (
                <ExternalToolCard key={tool.id} tool={tool} accent={group.accent} hasAccess={hasAccess} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SignedOutState() {
  return (
    <div style={styles.statePanel}>
      <div style={styles.stateIcon}>{'\u{1F510}'}</div>
      <div style={styles.stateTitle}>Sign in to VISINVOKE</div>
      <p style={styles.stateText}>
        The VISINVOKE Tech Hub requires a signed-in VISINVOKE account. Please sign in to continue.
      </p>
    </div>
  );
}

function NoAccessState() {
  return (
    <div style={styles.statePanel}>
      <div style={styles.stateIcon}>{'\u{1F6E1}'}</div>
      <div style={styles.stateTitle}>Technician Access required</div>
      <p style={styles.stateText}>
        The VISINVOKE Tech Hub provides technician utilities and reference tools for approved
        Technician Access accounts. Your account is signed in but does not currently have Technician
        Access. If you believe you should have access, contact VISINVOKE Support.
      </p>
    </div>
  );
}

export default function TechHubPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Technician Access uses the SAME source as the Technician tab (Manage.js):
  // effective_capabilities from GET /v1/technician-application/me. hub_access is
  // the capability that unlocks the Tech Hub. effective_capabilities is already
  // status-gated by the backend (all false unless the technician account is
  // active), so this single check means "active technician AND Hub Access".
  // Access defaults to locked while loading and on any request failure.
  const [techApp, setTechApp] = useState(null);
  useEffect(() => {
    let cancelled = false;
    if (!user) { setTechApp(null); return () => { cancelled = true; }; }
    API.get('/v1/technician-application/me')
      .then(r => { if (!cancelled) { setTechApp((r && r.data && r.data.application) ? r.data.application : null); } })
      .catch(() => { if (!cancelled) { setTechApp(null); } });
    return () => { cancelled = true; };
  }, [user]);

  const hasAccess = !!(
    techApp &&
    techApp.effective_capabilities &&
    techApp.effective_capabilities.hub_access === true
  );

  const [vw, setVw] = useState(typeof window !== 'undefined' ? window.innerWidth : 1440);
  useEffect(() => {
    const onResize = () => setVw(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const extColumns = extColumnsForWidth(vw);

  const openTool = slug => {
    navigate(TECH_HUB_BASE_ROUTE + '/' + slug);
  };

  // Correction stage: the catalogue is visible to every signed-in VISINVOKE
  // user. Technician Access controls the launcher-action state per card, not
  // catalogue visibility. Only a signed-out visitor sees the sign-in prompt.
  let body;
  if (!user) {
    body = <SignedOutState />;
  } else {
    body = (
      <div>
        {!hasAccess ? (
          <div style={styles.accessBanner}>
            <div style={styles.accessBannerTitle}>Technician Access required to run utilities</div>
            <p style={styles.accessBannerText}>
              You can browse every technician resource VISINVOKE offers below. Technician Access is required to run
              utilities through the VISINVOKE Desktop Command Center. To request Technician Access, contact VISINVOKE Support.
            </p>
          </div>
        ) : null}
        <div style={styles.groups}>
          {TECH_HUB_GROUPS.map(group => (
            <div key={group.id} style={styles.group}>
              <div style={styles.groupHeader}>
                <span style={{ ...styles.groupIcon, color: group.accent }}>{group.icon}</span>
                <h2 style={styles.groupTitle}>{group.title}</h2>
              </div>
              <div style={styles.grid}>
                {group.tools.map(tool => (
                  <ToolCard key={tool.slug} tool={tool} accent={group.accent} hasAccess={hasAccess} onOpen={openTool} />
                ))}
              </div>
            </div>
          ))}
        </div>

        <ExternalToolsSection hasAccess={hasAccess} columns={extColumns} />
      </div>
    );
  }

  return (
    <div style={styles.root}>
      <ProductNav />

      <div style={styles.hero}>
        <div style={styles.heroInner}>
          <div style={styles.heroEyebrow}>VISINVOKE Utilities</div>
          <h1 style={styles.heroTitle}>VISINVOKE Tech Utilities</h1>
          <p style={styles.heroSubtitle}>
            Technician utilities and reference resources for approved Technician Access accounts.
          </p>
          <p style={styles.heroIntro}>
            Tech Hub is designed for technicians who need fast access to common repair tools, support utilities and
            reference material from real client PCs. It reduces reliance on a USB toolkit and keeps the workflow centred
            around official sources and VISINVOKE resources.
          </p>
          <p style={styles.heroTagline}>
            Designed for everyday technician use, including workshop jobs, onsite repairs and client handovers.
          </p>
          {user ? (
            <div style={{ ...styles.accessBadge, ...glassSurface(hasAccess ? '#0E7A50' : '#A66D05', hasAccess ? '#34d399' : '#fbbf24', hasAccess ? '#6EE7B7' : '#FBBF24') }}>
              <span style={{ ...styles.accessDot, background: hasAccess ? '#34d399' : '#fbbf24' }} />
              {hasAccess ? 'Technician Access active' : 'Technician Access required'}
            </div>
          ) : null}
        </div>
      </div>

      <div style={styles.body}>{body}</div>
    </div>
  );
}

const styles = {
  root: {
    minHeight: '100vh',
    background: '#0d1117',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  hero: {
    background: 'linear-gradient(160deg, #0d1117 0%, #131c2e 50%, #0d1117 100%)',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    padding: '44px 0 34px',
  },
  heroInner: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: '0 40px',
    textAlign: 'center',
    boxSizing: 'border-box',
    width: '100%',
  },
  heroEyebrow: {
    fontSize: 13,
    fontWeight: 800,
    color: '#7dd3fc',
    textTransform: 'uppercase',
    letterSpacing: '0.18em',
    margin: '0 0 14px',
  },
  heroTitle: {
    fontSize: 46,
    fontWeight: 900,
    color: '#ffffff',
    lineHeight: 1.12,
    margin: '0 0 12px',
    letterSpacing: '-0.6px',
  },
  heroSubtitle: {
    fontSize: 18,
    fontWeight: 500,
    color: '#cbd5e1',
    lineHeight: 1.55,
    margin: '0 auto',
    maxWidth: 720,
  },
  heroIntro: {
    fontSize: 15,
    fontWeight: 400,
    color: '#94a3b8',
    lineHeight: 1.65,
    margin: '14px auto 0',
    maxWidth: 760,
  },
  heroTagline: {
    fontSize: 14,
    fontWeight: 700,
    color: '#5eead4',
    lineHeight: 1.5,
    margin: '12px auto 0',
    maxWidth: 760,
  },
  accessBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 18,
    borderRadius: 20,
    border: '1px solid',
    padding: '6px 14px',
    fontSize: 13,
    fontWeight: 800,
    letterSpacing: '0.04em',
  },
  accessDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    flexShrink: 0,
  },
  body: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: '36px 40px 72px',
    boxSizing: 'border-box',
    width: '100%',
  },
  groups: {
    display: 'flex',
    flexDirection: 'column',
    gap: 40,
  },
  group: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  groupHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
  },
  groupIcon: {
    fontSize: 24,
    lineHeight: 1,
  },
  groupTitle: {
    fontSize: 24,
    fontWeight: 800,
    color: '#ffffff',
    letterSpacing: '-0.3px',
    margin: 0,
    textAlign: 'center',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: 16,
  },
  card: {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: 14,
    padding: '18px 17px',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    textAlign: 'left',
  },
  cardSheen: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 70,
    pointerEvents: 'none',
    background: 'linear-gradient(180deg, rgba(255,255,255,0.12) 0%, transparent 100%)',
  },
  cardHeader: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: 11,
    zIndex: 1,
  },
  cardIcon: {
    fontSize: 26,
    lineHeight: 1,
    flexShrink: 0,
    filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.4))',
  },
  cardTitle: {
    fontSize: 17,
    fontWeight: 800,
    color: '#ffffff',
    lineHeight: 1.25,
    letterSpacing: '-0.2px',
    textShadow: '0 1px 3px rgba(0,0,0,0.35)',
  },
  cardDesc: {
    position: 'relative',
    fontSize: 13,
    color: 'rgba(255,255,255,0.9)',
    lineHeight: 1.55,
    flex: 1,
    zIndex: 1,
  },
  cardFooter: {
    position: 'relative',
    marginTop: 'auto',
    paddingTop: 12,
    display: 'flex',
    flexDirection: 'column',
    gap: 11,
    zIndex: 1,
  },
  statusRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 7,
  },
  statusDot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    flexShrink: 0,
  },
  statusText: {
    fontSize: 11,
    fontWeight: 800,
    color: '#ffffff',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },
  cardHint: {
    fontSize: 11,
    fontWeight: 600,
    color: 'rgba(255,255,255,0.7)',
    letterSpacing: '0.02em',
  },
  openBtn: {
    borderRadius: 9,
    padding: '10px 14px',
    fontSize: 13,
    fontWeight: 700,
    width: '100%',
  },
  accessBanner: {
    width: '100%',
    boxSizing: 'border-box',
    margin: '0 0 32px',
    padding: '22px 24px',
    borderRadius: 14,
    border: '1px solid ' + hexA('#fbbf24', 0.45),
    background: 'linear-gradient(160deg, ' + hexA('#fbbf24', 0.12) + ' 0%, ' + hexA('#78350f', 0.30) + ' 100%)',
    boxShadow: '0 0 0 1px ' + hexA('#fbbf24', 0.12) + ', 0 6px 20px rgba(0,0,0,0.35)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
  accessBannerTitle: {
    fontSize: 16,
    fontWeight: 800,
    color: '#fcd34d',
    marginBottom: 8,
  },
  accessBannerText: {
    fontSize: 14,
    color: '#e5e7eb',
    lineHeight: 1.65,
    margin: 0,
    maxWidth: 820,
  },
  statePanel: {
    maxWidth: 640,
    margin: '20px auto 0',
    background: 'linear-gradient(160deg, #101a2e 0%, #16243d 100%)',
    border: '1px solid rgba(255,255,255,0.10)',
    borderRadius: 14,
    padding: '40px 34px',
    textAlign: 'center',
    boxShadow: '0 6px 24px rgba(0,0,0,0.42)',
  },
  stateIcon: {
    fontSize: 40,
    lineHeight: 1,
    marginBottom: 16,
  },
  stateTitle: {
    fontSize: 22,
    fontWeight: 800,
    color: '#ffffff',
    marginBottom: 12,
  },
  stateText: {
    fontSize: 15,
    color: '#cbd5e1',
    lineHeight: 1.7,
    margin: 0,
  },
};

// External Tool Downloads styles. Fixed-zone card metrics keep every card in a
// grid aligned regardless of title or text length.
const extStyles = {
  section: {
    marginTop: 56,
    paddingTop: 40,
    borderTop: '1px solid rgba(255,255,255,0.10)',
  },
  sectionHead: {
    marginBottom: 22,
    textAlign: 'center',
  },
  sectionEyebrow: {
    fontSize: 12,
    fontWeight: 800,
    color: '#7dd3fc',
    textTransform: 'uppercase',
    letterSpacing: '0.18em',
    margin: '0 0 10px',
  },
  sectionTitle: {
    fontSize: 30,
    fontWeight: 900,
    color: '#ffffff',
    letterSpacing: '-0.4px',
    margin: '0 0 12px',
  },
  sectionHelper: {
    fontSize: 15,
    color: '#cbd5e1',
    lineHeight: 1.65,
    margin: '0 0 8px',
    maxWidth: 900,
    textAlign: 'center',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  sectionCount: {
    fontSize: 13,
    fontWeight: 700,
    color: 'rgba(203,213,225,0.75)',
    margin: 0,
  },
  notice: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 14,
    padding: '16px 18px',
    borderRadius: 14,
    margin: '0 0 16px',
  },
  noticeIcon: {
    fontSize: 22,
    lineHeight: 1,
    flexShrink: 0,
  },
  noticeTitle: {
    fontSize: 15,
    fontWeight: 800,
    color: '#7dd3fc',
    marginBottom: 6,
  },
  noticeText: {
    fontSize: 14,
    color: '#cbd5e1',
    lineHeight: 1.6,
    margin: 0,
  },
  lockedHelper: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    padding: '14px 18px',
    borderRadius: 14,
    margin: '0 0 30px',
  },
  lockedHelperIcon: {
    fontSize: 18,
    lineHeight: 1,
    flexShrink: 0,
  },
  lockedHelperText: {
    fontSize: 14,
    color: '#fde68a',
    lineHeight: 1.6,
    margin: 0,
    fontWeight: 600,
  },
  groups: {
    display: 'flex',
    flexDirection: 'column',
    gap: 44,
  },
  group: {
    display: 'flex',
    flexDirection: 'column',
    gap: 18,
  },
  groupHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    gap: 14,
    paddingLeft: 14,
  },
  groupIcon: {
    fontSize: 26,
    lineHeight: 1.1,
    flexShrink: 0,
  },
  groupTitle: {
    fontSize: 22,
    fontWeight: 800,
    color: '#ffffff',
    letterSpacing: '-0.3px',
    margin: '0 0 4px',
    textAlign: 'center',
  },
  groupHelper: {
    fontSize: 13.5,
    color: '#94a3b8',
    lineHeight: 1.55,
    margin: 0,
    maxWidth: 820,
    textAlign: 'center',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  grid: {
    display: 'grid',
    gap: 18,
    alignItems: 'stretch',
  },
  card: {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: 16,
    padding: '18px 17px 17px',
    minHeight: 480,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'left',
    boxSizing: 'border-box',
  },
  cardSheen: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 74,
    pointerEvents: 'none',
    background: 'linear-gradient(180deg, rgba(255,255,255,0.12) 0%, transparent 100%)',
  },
  accentBar: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    pointerEvents: 'none',
  },
  zoneHeader: {
    position: 'relative',
    display: 'flex',
    alignItems: 'flex-start',
    gap: 11,
    minHeight: 52,
    zIndex: 1,
  },
  headerIcon: {
    fontSize: 26,
    lineHeight: 1.2,
    flexShrink: 0,
    filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.4))',
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: 800,
    color: '#ffffff',
    lineHeight: 1.25,
    letterSpacing: '-0.2px',
    textShadow: '0 1px 3px rgba(0,0,0,0.35)',
  },
  zoneCategory: {
    position: 'relative',
    minHeight: 30,
    display: 'flex',
    alignItems: 'center',
    marginTop: 6,
    zIndex: 1,
  },
  categoryPill: {
    fontSize: 11,
    fontWeight: 700,
    color: '#ffffff',
    background: 'rgba(13,17,23,0.28)',
    borderRadius: 20,
    padding: '3px 10px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  zoneDesc: {
    position: 'relative',
    fontSize: 13,
    color: 'rgba(255,255,255,0.92)',
    lineHeight: 1.5,
    marginTop: 10,
    minHeight: 104,
    zIndex: 1,
  },
  zoneTechUse: {
    position: 'relative',
    marginTop: 10,
    minHeight: 96,
    zIndex: 1,
  },
  techUseLabel: {
    fontSize: 10,
    fontWeight: 800,
    color: 'rgba(255,255,255,0.72)',
    textTransform: 'uppercase',
    letterSpacing: '0.09em',
    marginBottom: 3,
  },
  techUseText: {
    fontSize: 12.5,
    color: 'rgba(255,255,255,0.86)',
    lineHeight: 1.5,
  },
  zoneBadges: {
    position: 'relative',
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    alignContent: 'flex-start',
    marginTop: 10,
    minHeight: 46,
    zIndex: 1,
  },
  badge: {
    fontSize: 10.5,
    fontWeight: 700,
    borderRadius: 7,
    padding: '3px 8px',
    letterSpacing: '0.02em',
    lineHeight: 1.3,
  },
  zoneFooter: {
    position: 'relative',
    marginTop: 'auto',
    paddingTop: 12,
    borderTop: '1px solid rgba(255,255,255,0.18)',
    display: 'flex',
    flexDirection: 'column',
    gap: 9,
    zIndex: 1,
  },
  footerHint: {
    fontSize: 10.5,
    fontWeight: 600,
    color: 'rgba(255,255,255,0.7)',
    lineHeight: 1.4,
    minHeight: 30,
  },
  actionBtn: {
    display: 'block',
    width: '100%',
    boxSizing: 'border-box',
    borderRadius: 9,
    padding: '10px 14px',
    fontSize: 13,
    fontWeight: 700,
    textAlign: 'center',
  },
};
